import * as React from 'react';
import axios from 'axios';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Grid,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import useAuthorize from '../hooks/useAuthorize';
import { SEO_PORTAL_ROLES } from '../roles/portalRoles';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import SeoGrowthTrendChart from './SeoGrowthTrendChart';

type RankingRow = {
    site_url: string;
    week_start_date: string;
    week_end_date: string;
    data_date: string;
    keyword: string;
    page_url: string;
    country: string;
    device: string;
    clicks: number;
    impressions: number;
    ctr: number;
    ctr_percentage: number;
    average_position: number | null;
    approx_serp_page: number | null;
    ranking_zone: string;
    source_partner: string;
};

type MovementRow = {
    keyword: string;
    page_url: string;
    country: string;
    device: string;
    previous_average_position: number | null;
    current_average_position: number | null;
    position_improvement: number | null;
    clicks: number;
    impressions: number;
    previous_clicks: number | null;
    previous_impressions: number | null;
    ctr_percentage: number;
    previous_ctr_percentage: number | null;
    clicks_change: number | null;
    impressions_change: number | null;
    ctr_change: number | null;
    approx_serp_page: number | null;
    ranking_zone: string;
    current_date: string;
    previous_date: string;
};

type WeeklySummaryRow = {
    data_date: string;
    week_start_date: string;
    week_end_date: string;
    keyword_rows: number;
    keyword_count: number;
    clicks: number;
    impressions: number;
    ctr_percentage: number;
    average_position: number | null;
    impressions_change: number | null;
    clicks_change: number | null;
    ctr_change: number | null;
    position_change: number | null;
};

type SyncStatus = {
    latest_data_date: string | null;
    latest_week_end_date: string | null;
    last_sync_at: string | null;
    snapshot_count: number;
    total_rows: number;
    comparison_current_date: string | null;
    comparison_previous_date: string | null;
};

type ListResponse<T> = {
    note?: string;
    count: number;
    data: T[];
    comparison?: {
        current_date: string | null;
        previous_date: string | null;
        auto_selected: boolean;
    };
};

type SyncStatusResponse = {
    note?: string;
    schedule?: string;
    data: SyncStatus;
};

const getApiBase = (): string => {
    const root = String(getApiBaseUrl() || '').trim();
    if (!root) return '/api';
    if (root.endsWith('/api')) return root;
    if (root.endsWith('/')) return `${root}api`;
    return `${root}/api`;
};

const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('user_authToken');
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
};

const formatNumber = (value: number | null | undefined, digits = 2): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    return Number(value).toFixed(digits);
};

const formatInteger = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    return Number(value).toLocaleString();
};

const formatSigned = (value: number | null | undefined, digits = 2): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    const shown = Number(value);
    const sign = shown > 0 ? '+' : '';
    return `${sign}${shown.toFixed(digits)}`;
};

const formatSignedInteger = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    const shown = Number(value);
    const sign = shown > 0 ? '+' : '';
    return `${sign}${shown.toLocaleString()}`;
};

const formatWeekLabel = (row: { week_start_date?: string; week_end_date?: string; data_date: string }): string => {
    if (row.week_start_date && row.week_end_date) {
        return `${row.week_start_date} → ${row.week_end_date}`;
    }
    return row.data_date;
};

const formatSyncTime = (value: string | null | undefined): string => {
    if (!value) return 'never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const addDays = (date: Date, days: number): Date => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const toIso = (date: Date): string => date.toISOString().slice(0, 10);

const DeltaChip: React.FC<{
    value: number | null | undefined;
    positiveIsGood?: boolean;
    suffix?: string;
    integer?: boolean;
}> = ({ value, positiveIsGood = true, suffix = '', integer = false }) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return <Typography variant="caption" color="text.secondary">--</Typography>;
    }
    const shown = Number(value);
    if (shown === 0) {
        return (
            <Chip
                size="small"
                icon={<TrendingFlatIcon />}
                label={integer ? '0' : '0.00'}
                variant="outlined"
            />
        );
    }
    const isGood = positiveIsGood ? shown > 0 : shown < 0;
    const icon = shown > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />;
    const label = integer ? formatSignedInteger(shown) : `${formatSigned(shown)}${suffix}`;
    return (
        <Chip
            size="small"
            icon={icon}
            label={label}
            color={isGood ? 'success' : 'error'}
            variant="outlined"
        />
    );
};

const DashboardSeo: React.FC = () => {
    useAuthorize(SEO_PORTAL_ROLES);
    const apiBase = React.useMemo(() => getApiBase(), []);

    const defaultEndDate = React.useMemo(() => toIso(addDays(new Date(), -3)), []);
    const defaultStartDate = React.useMemo(() => toIso(addDays(new Date(), -90)), []);

    const [startDate, setStartDate] = React.useState<string>(defaultStartDate);
    const [endDate, setEndDate] = React.useState<string>(defaultEndDate);
    const [keywordFilter, setKeywordFilter] = React.useState<string>('');
    const [country, setCountry] = React.useState<string>('in');
    const [device, setDevice] = React.useState<string>('');
    const [weeklySummary, setWeeklySummary] = React.useState<WeeklySummaryRow[]>([]);
    const [latestRankings, setLatestRankings] = React.useState<RankingRow[]>([]);
    const [movement, setMovement] = React.useState<MovementRow[]>([]);
    const [syncStatus, setSyncStatus] = React.useState<SyncStatus | null>(null);
    const [comparisonDates, setComparisonDates] = React.useState<{ current: string | null; previous: string | null }>({
        current: null,
        previous: null,
    });
    const [note, setNote] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string>('');

    const loadData = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                startDate,
                endDate,
                keyword: keywordFilter || undefined,
                country: country || undefined,
                device: device || undefined,
            };

            const [summaryRes, syncRes, movementRes] = await Promise.all([
                axios.get<ListResponse<WeeklySummaryRow>>(`${apiBase}/seo/weekly-summary`, {
                    params: { ...params, limit: 52 },
                    headers: getAuthHeaders(),
                }),
                axios.get<SyncStatusResponse>(`${apiBase}/seo/sync-status`, {
                    params: { country: country || undefined, device: device || undefined },
                    headers: getAuthHeaders(),
                }),
                axios.get<ListResponse<MovementRow>>(`${apiBase}/seo/keyword-movement`, {
                    params: {
                        ...params,
                        autoCompare: true,
                        limit: 500,
                    },
                    headers: getAuthHeaders(),
                }),
            ]);

            const summaryRows = summaryRes.data.data || [];
            setWeeklySummary(summaryRows);
            setSyncStatus(syncRes.data.data || null);
            setMovement(movementRes.data.data || []);
            setComparisonDates({
                current: movementRes.data.comparison?.current_date || syncRes.data.data?.comparison_current_date || null,
                previous: movementRes.data.comparison?.previous_date || syncRes.data.data?.comparison_previous_date || null,
            });
            setNote(summaryRes.data.note || syncRes.data.note || movementRes.data.note || '');

            const latestWeekDate = summaryRows.length > 0
                ? summaryRows[summaryRows.length - 1].data_date
                : syncRes.data.data?.latest_data_date;

            if (latestWeekDate) {
                const rankingsRes = await axios.get<ListResponse<RankingRow>>(`${apiBase}/seo/rankings`, {
                    params: {
                        startDate: latestWeekDate,
                        endDate: latestWeekDate,
                        keyword: keywordFilter || undefined,
                        country: country || undefined,
                        device: device || undefined,
                        limit: 500,
                    },
                    headers: getAuthHeaders(),
                });
                setLatestRankings(rankingsRes.data.data || []);
            } else {
                setLatestRankings([]);
            }
        } catch (err: any) {
            const message = err?.response?.data?.error || err?.message || 'Failed to load SEO dashboard';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [apiBase, startDate, endDate, keywordFilter, country, device]);

    React.useEffect(() => {
        void loadData();
    }, [loadData]);

    const latestWeek = weeklySummary.length > 0 ? weeklySummary[weeklySummary.length - 1] : null;
    const previousWeek = weeklySummary.length > 1 ? weeklySummary[weeklySummary.length - 2] : null;

    const correctedMovementStats = React.useMemo(() => {
        const improving = movement.filter((row) => (Number(row.position_improvement) || 0) > 0).length;
        const declining = movement.filter((row) => (Number(row.position_improvement) || 0) < 0).length;
        const flat = movement.length - improving - declining;
        return { improving, declining, flat };
    }, [movement]);

    return (
        <Box sx={{ p: 2 }}>
            <Card>
                <CardHeader
                    title="SEO Growth"
                    subheader="Week-by-week Google Search Console performance for monitored keywords (India)."
                />
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            type="date"
                            label="Trend from"
                            value={startDate}
                            onChange={(event) => setStartDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="Trend to"
                            value={endDate}
                            onChange={(event) => setEndDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            size="small"
                            label="Keyword filter"
                            placeholder="credco, pp m12, hdpe..."
                            value={keywordFilter}
                            onChange={(event) => setKeywordFilter(event.target.value)}
                            sx={{ minWidth: 220 }}
                        />
                        <Select
                            size="small"
                            value={country}
                            onChange={(event) => setCountry(String(event.target.value))}
                            sx={{ minWidth: 110 }}
                        >
                            <MenuItem value="in">India</MenuItem>
                        </Select>
                        <Select
                            size="small"
                            displayEmpty
                            value={device}
                            onChange={(event) => setDevice(String(event.target.value))}
                            sx={{ minWidth: 140 }}
                        >
                            <MenuItem value="">All Devices</MenuItem>
                            <MenuItem value="desktop">Desktop</MenuItem>
                            <MenuItem value="mobile">Mobile</MenuItem>
                            <MenuItem value="tablet">Tablet</MenuItem>
                        </Select>
                        <Button variant="contained" onClick={() => void loadData()}>
                            Apply
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setKeywordFilter('');
                                setDevice('');
                                setCountry('in');
                                setStartDate(defaultStartDate);
                                setEndDate(defaultEndDate);
                            }}
                        >
                            Reset
                        </Button>
                    </Stack>

                    {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Data syncs automatically every <strong>Monday ~1:00 AM IST</strong> from Google Search Console.
                            {syncStatus?.last_sync_at ? (
                                <> Last import: <strong>{formatSyncTime(syncStatus.last_sync_at)}</strong>.</>
                            ) : null}
                            {syncStatus?.snapshot_count ? (
                                <> {syncStatus.snapshot_count} weekly snapshot{syncStatus.snapshot_count === 1 ? '' : 's'} on file.</>
                            ) : (
                                <> No weekly snapshots yet — the first automated sync will populate this dashboard.</>
                            )}
                        </Typography>
                        {comparisonDates.current && comparisonDates.previous ? (
                            <Typography variant="caption" color="text.secondary">
                                Comparing latest week ending <strong>{comparisonDates.current}</strong> vs previous week ending <strong>{comparisonDates.previous}</strong>.
                            </Typography>
                        ) : null}
                    </Alert>

                    {latestWeek ? (
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Tooltip title="Total Search Console impressions in the latest weekly snapshot.">
                                        <Typography variant="body2" color="text.secondary">Impressions (latest week)</Typography>
                                    </Tooltip>
                                    <Typography variant="h6">{formatInteger(latestWeek.impressions)}</Typography>
                                    <DeltaChip value={latestWeek.impressions_change} integer />
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Tooltip title="Total clicks in the latest weekly snapshot.">
                                        <Typography variant="body2" color="text.secondary">Clicks (latest week)</Typography>
                                    </Tooltip>
                                    <Typography variant="h6">{formatInteger(latestWeek.clicks)}</Typography>
                                    <DeltaChip value={latestWeek.clicks_change} integer />
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Tooltip title="Clicks ÷ impressions for the latest week. True CTR, not an average of keyword CTRs.">
                                        <Typography variant="body2" color="text.secondary">CTR (latest week)</Typography>
                                    </Tooltip>
                                    <Typography variant="h6">{formatNumber(latestWeek.ctr_percentage)}%</Typography>
                                    <DeltaChip value={latestWeek.ctr_change} suffix="%" />
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Tooltip title="Impression-weighted average rank from GSC. Lower is better (1 ≈ top of page 1).">
                                        <Typography variant="body2" color="text.secondary">Avg position (latest week)</Typography>
                                    </Tooltip>
                                    <Typography variant="h6">{formatNumber(latestWeek.average_position)}</Typography>
                                    <DeltaChip
                                        value={latestWeek.position_change}
                                        positiveIsGood
                                    />
                                    {previousWeek?.average_position != null ? (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            was {formatNumber(previousWeek.average_position)}
                                        </Typography>
                                    ) : null}
                                </Paper>
                            </Grid>
                        </Grid>
                    ) : !loading ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            No weekly SEO snapshots in this date range. Data appears after the automated Monday sync runs.
                        </Alert>
                    ) : null}

                    <SeoGrowthTrendChart data={weeklySummary} />

                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Weekly growth trend</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        One row per synced week. Change columns show movement vs the prior week in the table.
                    </Typography>
                    <Table size="small" sx={{ mb: 3 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Week</TableCell>
                                <TableCell align="right">Keywords</TableCell>
                                <TableCell align="right">Impressions</TableCell>
                                <TableCell align="right">Δ Impressions</TableCell>
                                <TableCell align="right">Clicks</TableCell>
                                <TableCell align="right">Δ Clicks</TableCell>
                                <TableCell align="right">CTR</TableCell>
                                <TableCell align="right">Δ CTR</TableCell>
                                <TableCell align="right">Avg position</TableCell>
                                <TableCell align="right">Δ Position</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && weeklySummary.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10}>
                                        <Typography variant="body2" color="text.secondary">
                                            No weekly snapshots in this range.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {[...weeklySummary].reverse().map((row) => (
                                <TableRow key={row.data_date}>
                                    <TableCell>{formatWeekLabel(row)}</TableCell>
                                    <TableCell align="right">{formatInteger(row.keyword_count)}</TableCell>
                                    <TableCell align="right">{formatInteger(row.impressions)}</TableCell>
                                    <TableCell align="right">{formatSignedInteger(row.impressions_change)}</TableCell>
                                    <TableCell align="right">{formatInteger(row.clicks)}</TableCell>
                                    <TableCell align="right">{formatSignedInteger(row.clicks_change)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.ctr_percentage)}%</TableCell>
                                    <TableCell align="right">{formatSigned(row.ctr_change)}%</TableCell>
                                    <TableCell align="right">{formatNumber(row.average_position)}</TableCell>
                                    <TableCell align="right">{formatSigned(row.position_change)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {comparisonDates.current && comparisonDates.previous ? (
                        <>
                            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="subtitle1">
                                    Keyword changes ({comparisonDates.previous} → {comparisonDates.current})
                                </Typography>
                                <Chip size="small" color="success" label={`${correctedMovementStats.improving} improving`} />
                                <Chip size="small" color="error" label={`${correctedMovementStats.declining} declining`} />
                                <Chip size="small" label={`${correctedMovementStats.flat} flat`} />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Position change is positive when rank improves (moves toward #1). Traffic columns compare the same two weeks.
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Keyword changes</Typography>
                    )}

                    <Table size="small" sx={{ mb: 3 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Keyword</TableCell>
                                <TableCell>Page</TableCell>
                                <TableCell align="right">Prev position</TableCell>
                                <TableCell align="right">Curr position</TableCell>
                                <TableCell align="right">Δ Position</TableCell>
                                <TableCell align="right">Impressions</TableCell>
                                <TableCell align="right">Δ Impressions</TableCell>
                                <TableCell align="right">Clicks</TableCell>
                                <TableCell align="right">Δ Clicks</TableCell>
                                <TableCell align="right">CTR</TableCell>
                                <TableCell align="right">Δ CTR</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && movement.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11}>
                                        <Typography variant="body2" color="text.secondary">
                                            Need at least two weekly snapshots to show keyword movement.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {movement.map((row, idx) => (
                                <TableRow key={`${row.keyword}-${row.page_url}-${idx}`}>
                                    <TableCell>{row.keyword}</TableCell>
                                    <TableCell sx={{ maxWidth: 320, wordBreak: 'break-all' }}>{row.page_url}</TableCell>
                                    <TableCell align="right">{formatNumber(row.previous_average_position)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.current_average_position)}</TableCell>
                                    <TableCell align="right">{formatSigned(row.position_improvement)}</TableCell>
                                    <TableCell align="right">{formatInteger(row.impressions)}</TableCell>
                                    <TableCell align="right">{formatSignedInteger(row.impressions_change)}</TableCell>
                                    <TableCell align="right">{formatInteger(row.clicks)}</TableCell>
                                    <TableCell align="right">{formatSignedInteger(row.clicks_change)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.ctr_percentage)}%</TableCell>
                                    <TableCell align="right">{formatSigned(row.ctr_change)}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                        Latest week keyword snapshot
                        {latestWeek ? ` (${formatWeekLabel(latestWeek)})` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Per-keyword breakdown for the most recent synced week. SERP page ≈ which results page you appear on (position ÷ 10, rounded up).
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Keyword</TableCell>
                                <TableCell>Page</TableCell>
                                <TableCell>Device</TableCell>
                                <TableCell align="right">Avg position</TableCell>
                                <TableCell align="right">SERP page</TableCell>
                                <TableCell>Zone</TableCell>
                                <TableCell align="right">Impressions</TableCell>
                                <TableCell align="right">Clicks</TableCell>
                                <TableCell align="right">CTR</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && latestRankings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <Typography variant="body2" color="text.secondary">
                                            No keyword rows for the latest week.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {latestRankings.map((row, idx) => (
                                <TableRow key={`${row.keyword}-${row.page_url}-${idx}`}>
                                    <TableCell>{row.keyword}</TableCell>
                                    <TableCell sx={{ maxWidth: 360, wordBreak: 'break-all' }}>{row.page_url}</TableCell>
                                    <TableCell>{row.device}</TableCell>
                                    <TableCell align="right">{formatNumber(row.average_position)}</TableCell>
                                    <TableCell align="right">{formatInteger(row.approx_serp_page)}</TableCell>
                                    <TableCell>{row.ranking_zone}</TableCell>
                                    <TableCell align="right">{formatInteger(row.impressions)}</TableCell>
                                    <TableCell align="right">{formatInteger(row.clicks)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.ctr_percentage)}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {note ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                            {note}
                        </Typography>
                    ) : null}
                </CardContent>
            </Card>
        </Box>
    );
};

export default DashboardSeo;
